import { Reveal } from "@/components/Reveal";
import { scrollToSection } from "@/App";

const QuoteLink = ({ testid }) => (
  <button
    data-testid={testid}
    onClick={() => scrollToSection("#contact")}
    className="mt-5 w-fit font-mono text-[10px] tracking-[0.25em] uppercase text-[#7FB3E8] hover:text-white transition-colors duration-300"
  >
    Request quote →
  </button>
);

const SpecRow = ({ k, v }) => (
  <tr className="border-b border-white/10 last:border-b-0">
    <td className="py-2.5 pr-4 font-mono text-[10px] md:text-xs tracking-[0.2em] uppercase text-smoke">{k}</td>
    <td className="py-2.5 font-mono text-xs md:text-sm text-white text-right">{v}</td>
  </tr>
);

const CardShell = ({ testid, children }) => (
  <Reveal>
    <article
      data-testid={testid}
      className="group h-full bg-steel border border-white/10 hover:border-[#7FB3E8]/40 transition-colors duration-300 flex flex-col"
    >
      <span className="block h-0.5 w-0 bg-[#7FB3E8] group-hover:w-full transition-[width] duration-500" />
      {children}
    </article>
  </Reveal>
);

const ImageFrame = ({ src, alt, caption }) => (
  <div>
    <div className="bg-white m-4 mb-0 p-4 overflow-hidden">
      <img
        src={src}
        alt={alt}
        className="w-full h-44 md:h-52 object-contain group-hover:scale-[1.04] transition-transform duration-500"
      />
    </div>
    <p className="px-6 pt-3 font-mono text-[10px] tracking-[0.25em] uppercase text-smoke">{caption}</p>
  </div>
);

export const Products = () => {
  return (
    <section id="products" data-testid="products-section" className="relative bg-ink py-20 md:py-28 border-t border-white/10">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10">
        <Reveal>
          <div className="mb-12 md:mb-16 text-center">
            <p className="font-mono text-[10px] md:text-xs tracking-[0.35em] uppercase text-smoke mb-4">
              Our Products
            </p>
            <h2 className="font-heading font-semibold uppercase text-3xl md:text-5xl tracking-wide text-white">
              Cable Trays & Accessories
            </h2>
            <p className="mt-5 max-w-xl mx-auto font-body text-sm md:text-base text-smoke leading-relaxed">
              Standard schedules ready for dispatch. Non-standard drawings welcome.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          <CardShell testid="product-card-perforated">
            <ImageFrame
              src="/assets/tray-perforated.png"
              alt="Perforated cable tray — D V Steels"
              caption="Ventilated base, punched pattern"
            />
            <div className="p-6 md:p-8 pt-4 flex-1 flex flex-col">
              <h3 className="font-heading font-semibold uppercase text-2xl tracking-wide text-white">
                Perforated Cable Tray
              </h3>
              <table className="mt-5 w-full border-t border-white/10" data-testid="spec-table-perforated">
                <tbody>
                  <SpecRow k="Width" v="50 — 600 mm" />
                  <SpecRow k="Side Height" v="25 — 100 mm" />
                  <SpecRow k="Thickness" v="1.2 / 1.6 / 2.0 / 2.5 mm" />
                  <SpecRow k="Length" v="2.5 m / 3 m standard" />
                  <SpecRow k="Finish" v="PG • HDG • Powder Coated" />
                </tbody>
              </table>
              <QuoteLink testid="product-quote-perforated" />
            </div>
          </CardShell>

          <CardShell testid="product-card-ladder">
            <ImageFrame
              src="/assets/trays-frame.png"
              alt="Ladder type cable tray — D V Steels"
              caption="Heavy-duty ladder rungs"
            />
            <div className="p-6 md:p-8 pt-4 flex-1 flex flex-col">
              <h3 className="font-heading font-semibold uppercase text-2xl tracking-wide text-white">
                Ladder Type Tray
              </h3>
              <table className="mt-5 w-full border-t border-white/10" data-testid="spec-table-ladder">
                <tbody>
                  <SpecRow k="Width" v="100 — 1200 mm" />
                  <SpecRow k="Rung Spacing" v="250 / 300 mm" />
                  <SpecRow k="Thickness" v="1.6 / 2.0 / 2.5 mm" />
                  <SpecRow k="Duty" v="Medium / Heavy" />
                </tbody>
              </table>
              <QuoteLink testid="product-quote-ladder" />
            </div>
          </CardShell>

          <CardShell testid="product-card-accessories">
            <div className="p-6 md:p-8 flex-1 flex flex-col">
              <h3 className="font-heading font-semibold uppercase text-2xl tracking-wide text-white">
                Accessories & Fittings
              </h3>
              <p className="mt-3 font-body text-sm text-smoke leading-relaxed">
                Every turn, drop and junction — fabricated to match your tray schedule.
              </p>
              <ul className="mt-5 border-t border-white/10 font-mono text-xs md:text-sm text-white" data-testid="accessories-list">
                {["Horizontal / Vertical Bends", "Tees & Crosses", "Reducers — L/R & Centre", "Coupler Plates & Hardware", "Covers & Clamps"].map((a) => (
                  <li key={a} className="py-2.5 border-b border-white/10 flex items-center gap-3">
                    <span className="text-[#7FB3E8]">//</span> {a}
                  </li>
                ))}
              </ul>
            </div>
          </CardShell>

          <CardShell testid="product-card-finishes">
            <div className="p-6 md:p-8 flex-1 flex flex-col">
              <h3 className="font-heading font-semibold uppercase text-2xl tracking-wide text-white">
                Materials & Finishes
              </h3>
              <div className="mt-5 border-t border-white/10" data-testid="finishes-grid">
                {[
                  { t: "Pre-Galvanized", d: "IS 277 coated coil, clean mill finish for indoor runs." },
                  { t: "Hot-Dip Galvanized", d: "IS 4759 post-fabrication dip for corrosive outdoor duty." },
                  { t: "Powder Coated", d: "Colour-matched epoxy-polyester for visible installations." },
                ].map((f) => (
                  <div key={f.t} className="py-4 border-b border-white/10">
                    <p className="font-heading font-semibold uppercase text-lg tracking-wide text-white">{f.t}</p>
                    <p className="mt-1 font-body text-xs text-smoke leading-relaxed">{f.d}</p>
                  </div>
                ))}
              </div>
              <p className="mt-5 font-mono text-[10px] md:text-xs tracking-[0.25em] uppercase text-smoke">
                Base steel — HR / CR / GI • 1.2 to 2.5 mm • MTC on request
              </p>
            </div>
          </CardShell>
        </div>
      </div>
    </section>
  );
};
